/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Card,
  Chip,
  Grid,
  Link,
  Typography,
  useTheme,
  IconButton,
  Skeleton,
} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {useEffect, useMemo, useState} from 'react';
import {useInView} from '@jobseeker/lotus/hooks/observer';
import type {
  AdsElementType,
  AdsEventInfo,
  AppState,
} from '@jobseeker/common-core/types';
import {
  getUbaPageName,
  handleTracking,
  parseShowcaseUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {shallowEqual, useSelector} from 'react-redux';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import {ArrowBackIos, ArrowForwardIos} from '@mui/icons-material';
import {useMediaQuery} from '@mui/material';

export type GoldPartnersProps = {
  label?: string;
  titleOut?: boolean;
  adsEventInfo?: AdsEventInfo;
  slot: string;
};

const BORDER = {
  borderRadius: '16px',
  border: '1px solid',
  borderColor: 'border.main',
  background: '#FFF',
  boxShadow: '0 1.2px 0 0 #F3F7FA',
};

const GoldPartners = ({
  label = 'Hiring Partners',
  titleOut = false,
  adsEventInfo,
  slot,
}: GoldPartnersProps) => {
  const theme = useTheme();
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);
  const [fetchedAds, setFetchedAds] = useState(Array<AdsElementType>);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAdsLoading, setIsAdsLoading] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const shouldLoad = Boolean(slot);
  useLoadAds(
    slot || '',
    setFetchedAds,
    {},
    false,
    shouldLoad,
    (loaded: boolean) => setIsAdsLoading(!loaded),
    shouldLoad
  );

  const allAds: AdsElementType[] = useMemo(() => {
    const items = (fetchedAds || []).filter(Boolean);
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }, [fetchedAds]);
  const slides: AdsElementType[][] = useMemo(() => {
    const pages: AdsElementType[][] = [];
    // Show 2 ads per slide on mobile, 8 on desktop
    const adsPerSlide = isMobile ? 2 : 8;

    for (let i = 0; i < allAds.length; i += adsPerSlide) {
      pages.push(allAds.slice(i, i + adsPerSlide));
    }
    return pages;
  }, [allAds, isMobile]);

  useEffect(() => {
    setCurrentSlide(0);
  }, [slides.length]);

  const trackGoldPartnersEvent = (args: {
    type: 'view' | 'click';
    companyId?: string | number;
    companyName?: string;
    items?: AdsElementType[];
  }) => {
    const {type, companyId, companyName, items} = args;
    try {
      const refParam =
        new URL(window.location.href).searchParams.get('ref') || '';
      const source = 'Logo';
      let companyIDSArray: Array<string | number> = [];
      let companyNamesArray: string[] = [];
      if (type === 'view') {
        const list = items || allAds;
        companyIDSArray = list
          .map(ad => ad?.companyId || parseShowcaseUrl(ad?.link))
          .filter(Boolean) as Array<string | number>;
        companyNamesArray = list
          .map(ad => ad?.title)
          .filter(Boolean) as string[];
      }
      const payload =
        type === 'view'
          ? {
              ref: refParam,
              source,
              companyIDSArray,
              companyNamesArray,
              page: getUbaPageName(),
              sessionUsername: `${sessionStorage.getItem('userId')}`,
            }
          : {
              ref: refParam,
              source,
              companyID: companyId || '',
              companyName: companyName || '',
              page: getUbaPageName(),
              sessionUsername: `${sessionStorage.getItem('userId')}`,
            };

      TrackEventUBA(
        type === 'view' ? 'companyLogosView' : 'companyLogosClick',
        type,
        payload
      );
    } catch (e) {
      console.error('UBA Event Tracking Error', e);
    }
  };

  useEffect(() => {
    if (!hasTrackedView && allAds.length) {
      trackGoldPartnersEvent({type: 'view', items: allAds});
      setHasTrackedView(true);
    }
  }, [allAds, hasTrackedView]);

  if (!allAds?.length && !isAdsLoading) return null;

  return (
    <>
      {titleOut && (
        <Box display="flex" alignItems="center" sx={{mb: 1.5}}>
          <Typography fontSize={16} fontWeight={700} color="secondary.main">
            {label}
          </Typography>
          {slides.length > 1 && (
            <Box
              sx={{
                ml: 'auto',
                display: 'flex',
                gap: 0.5,
              }}
            >
              <IconButton
                aria-label="previous"
                size="small"
                onClick={() => setCurrentSlide(s => Math.max(0, s - 1))}
                disabled={currentSlide === 0}
                sx={{
                  backgroundColor: 'white',
                  color: '#666',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                  width: 20,
                  height: 20,
                }}
              >
                <ArrowBackIos
                  sx={{fontSize: 12}} // scale icon to fit within 20×20
                />
              </IconButton>
              <IconButton
                aria-label="next"
                size="small"
                onClick={() =>
                  setCurrentSlide(s => Math.min(slides.length - 1, s + 1))
                }
                disabled={currentSlide === slides.length - 1}
                sx={{
                  backgroundColor: 'white',
                  color: '#666',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                  width: 20,
                  height: 20,
                }}
              >
                <ArrowForwardIos
                  sx={{fontSize: 12}} // scale icon to fit within 20×20
                />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      <Card
        sx={{
          borderRadius: titleOut ? 2 : 4,
          px: {xs: 1, sm: 1.5},
          py: {xs: 1.5, sm: 2},
          mb: titleOut ? 3.75 : 2,
          minWidth: {sm: 368, md: 'auto'},
          minHeight: {sm: 211, md: 'auto'},
        }}
        data-testid={`job_ads_banner_${label
          ?.toLowerCase()
          ?.replace(/ /g, '-')}`}
        id={`job_ads_banner_${label?.toLowerCase()?.replace(/ /g, '-')}`}
      >
        {!titleOut && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{mb: 2}}
          >
            <Typography fontSize={16} fontWeight={600} color="secondary.main">
              {label}
            </Typography>
            {slides.length > 1 && (
              <Box
                sx={{
                  ml: 'auto',
                  display: 'flex',
                  gap: 0.5,
                }}
              >
                <IconButton
                  aria-label="previous"
                  size="small"
                  onClick={() => setCurrentSlide(s => Math.max(0, s - 1))}
                  disabled={currentSlide === 0}
                  sx={{
                    boxShadow: '0 2px 0 0 #F3F7FA',
                    width: {xs: 24, sm: 32},
                    height: {xs: 24, sm: 32},
                    backgroundColor: '#fff',
                  }}
                >
                  <ArrowBackIos
                    fontSize={
                      theme.breakpoints.down('sm') ? 'small' : 'inherit'
                    }
                  />
                </IconButton>
                <IconButton
                  aria-label="next"
                  size="small"
                  onClick={() =>
                    setCurrentSlide(s => Math.min(slides.length - 1, s + 1))
                  }
                  disabled={currentSlide === slides.length - 1}
                  sx={{
                    boxShadow: '0 2px 0 0 #F3F7FA',
                    width: {xs: 24, sm: 32},
                    height: {xs: 24, sm: 32},
                    backgroundColor: '#fff',
                  }}
                >
                  <ArrowForwardIos
                    fontSize={
                      theme.breakpoints.down('sm') ? 'small' : 'inherit'
                    }
                  />
                </IconButton>
              </Box>
            )}
          </Box>
        )}

        {isAdsLoading ? (
          <Grid container spacing={{xs: 1, sm: 2}}>
            {Array.from({length: isMobile ? 2 : 8}).map((_, idx) => (
              <Grid item xs={6} sm={isMobile ? 6 : 3} key={`skeleton-${idx}`}>
                <Box
                  sx={{
                    ...BORDER,
                    p: 1.25,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection={'column'}
                    gap={1.5}
                    alignItems="flex-start"
                  >
                    <Skeleton
                      variant="rectangular"
                      width={{xs: 32, sm: 50}}
                      height={{xs: 32, sm: 50}}
                      sx={{borderRadius: '16px'}}
                    />
                    <Box flex={1} minWidth={0} width="100%">
                      <Skeleton
                        variant="text"
                        width="80%"
                        height={{xs: 12, sm: 16}}
                        sx={{mb: 0.5}}
                      />
                      <Skeleton
                        variant="text"
                        width="60%"
                        height={{xs: 10, sm: 12}}
                        sx={{mb: 0.5}}
                      />
                      <Skeleton
                        variant="text"
                        width="50%"
                        height={{xs: 10, sm: 12}}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{position: 'relative', overflow: 'hidden'}}>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                transform: `translateX(-${currentSlide * 100}%)`,
                transition: 'transform 300ms ease',
              }}
            >
              {slides.map((page, pageIndex) => (
                <Box
                  key={`gold-partners-slide-${pageIndex}`}
                  sx={{flex: '0 0 100%', minWidth: '100%'}}
                >
                  <Grid container spacing={{xs: 1, sm: 2}}>
                    {page.map((item, idx) => {
                      const imgUrl = item?.companyLogo || item?.imageUrl;
                      const companyId =
                        item?.companyId || parseShowcaseUrl(item?.link) || '';
                      const subtitle = item?.subText || item?.description || '';
                      const badgeText =
                        item?.ctaText &&
                        item.ctaText.toLowerCase().includes('role')
                          ? item.ctaText
                          : (item as any)?.badge;

                      return (
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          key={`gold-partner-${pageIndex}-${idx}`}
                        >
                          <Box sx={{position: 'relative'}}>
                            {badgeText && (
                              <Chip
                                label={badgeText}
                                color="info"
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  zIndex: 1,
                                  backgroundColor: theme.palette.info.light,
                                  color: theme.palette.info.dark,
                                  fontWeight: 600,
                                  borderRadius: '10px',
                                }}
                              />
                            )}

                            <Box
                              sx={{
                                ...BORDER,
                                p: 1.25,
                                marginLeft: 0,
                                backgroundColor: 'background.paper',
                                transition: 'background-color 200ms ease',
                                '&:hover': {
                                  backgroundColor: 'border.main',
                                },
                              }}
                            >
                              <Link
                                href={item?.link || '#'}
                                target="_blank"
                                rel="nofollow"
                                underline="none"
                                onClick={() => {
                                  trackGoldPartnersEvent({
                                    type: 'click',
                                    companyId,
                                    companyName: item?.title,
                                  });
                                }}
                                sx={{display: 'block'}}
                              >
                                <Box
                                  display="flex"
                                  flexDirection={'column'}
                                  gap={1.5}
                                  alignItems="flex-start"
                                >
                                  <Box
                                    sx={{
                                      ...BORDER,
                                      overflow: 'hidden',
                                      width: 50,
                                      height: 50,
                                      position: 'relative',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {imgUrl && (
                                      <Image
                                        src={imgUrl}
                                        alt={item?.title}
                                        fill
                                        style={{
                                          objectFit: 'contain',
                                        }}
                                        quality={100}
                                        blurDataURL={imgUrl}
                                        sizes="50px"
                                      />
                                    )}
                                  </Box>

                                  <Box flex={1} minWidth={0}>
                                    <Typography
                                      fontSize={12}
                                      fontWeight={700}
                                      color="secondary.main"
                                      noWrap
                                    >
                                      {item?.title}
                                    </Typography>
                                    {subtitle && (
                                      <Typography
                                        fontSize={10}
                                        fontWeight={400}
                                        lineHeight={1.2}
                                        color="text.secondary"
                                        sx={{
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {subtitle}
                                      </Typography>
                                    )}
                                    <Typography
                                      fontSize={10}
                                      fontWeight={700}
                                      color="primary.main"
                                      sx={{mt: 0.5}}
                                    >
                                      {item?.ctaText ||
                                        'Explore Office Culture'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Link>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Card>
    </>
  );
};

export default GoldPartners;
